/**
 * The Nótur tab: one <SheetViewer> per (file id, rev). Connector pins come from the annotation
 * layers (annotate/SheetWorkspace); in development builds the demo connectors (?demoConnectors=1) are added on top.
 *
 * The reading position is remembered per file id while the song page is open: a new revision
 * arriving in the background (refetch, 404 → invalidation) or switching sheets and back remounts
 * the viewer at the same page, offset and zoom instead of page 1. A file shown for the first time on
 * this song page opens at SheetViewerEnv.initialPage (song tabs: the page it was left at), else page 1.
 */
import { useCallback, useContext, useMemo, useRef, type Ref } from 'react';
import { useSearchParams } from 'react-router';
import type { SongFileDTO } from '../../shared/dto/library';
import { useT } from '../i18n';
import { demoConnectors } from './demo';
import { SheetViewerEnvContext } from './env';
import { SheetViewer, type EditTouchAction } from './SheetViewer';
import type { ConnectorPin, RenderPageOverlay, ViewerHandle, ViewerMode, ViewerPosition } from './types';

function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (!ref) return;
  if (typeof ref === 'function') ref(value);
  else ref.current = value;
}

export function SheetPane({
  file,
  active,
  onStaleRevision,
  mode = 'view',
  connectors,
  renderPageOverlay,
  viewerRef,
  pinsInteractive,
  onPinTap,
  editTouchAction,
  onPageChange,
}: {
  file: SongFileDTO;
  active: boolean;
  onStaleRevision: () => void;
  mode?: ViewerMode;
  connectors?: readonly ConnectorPin[];
  renderPageOverlay?: RenderPageOverlay;
  viewerRef?: Ref<ViewerHandle>;
  /** Edit mode: pins stay tappable (onPinTap) instead of inert. */
  pinsInteractive?: boolean;
  onPinTap?: (pin: ConnectorPin) => void;
  /** Edit mode: touch-action of the page boxes. */
  editTouchAction?: EditTouchAction;
  onPageChange?: (page: number, count: number) => void;
}) {
  const t = useT();
  const [params] = useSearchParams();
  // Pre-M4 demo pins, development builds only: `import.meta.env.DEV` is false in production, so the
  // check and demo.ts are dropped from the bundle and ?demoConnectors=1 does nothing there.
  const demo = import.meta.env.DEV && params.get('demoConnectors') === '1';
  const pageCount = file.pages?.length ?? 0;
  const allConnectors = useMemo(() => {
    const extra = demo ? demoConnectors(pageCount, { toEnd: t('viewer.demo_to_end'), toStart: t('viewer.demo_to_start') }) : [];
    return connectors ? [...connectors, ...extra] : extra;
  }, [demo, pageCount, t, connectors]);

  // Last position per file id; the attached handle remembers which file it belongs to.
  const positions = useRef(new Map<string, ViewerPosition>());
  const attached = useRef<{ fileId: string; handle: ViewerHandle } | null>(null);
  /** File ids already shown on this song page (initialPage is asked only the first time). */
  const shown = useRef(new Set<string>());
  const env = useContext(SheetViewerEnvContext);
  const live = useRef({ env, pageCount });
  live.current = { env, pageCount };
  const fileId = file.id;

  const setHandle = useCallback(
    (handle: ViewerHandle | null) => {
      const prev = attached.current;
      if (prev && prev.handle !== handle) {
        // Detached while the old viewer's DOM still exists (layout-effect cleanup): read it now.
        const pos = prev.handle.getPosition();
        if (pos) positions.current.set(prev.fileId, pos);
      }
      attached.current = handle ? { fileId, handle } : null;
      assignRef(viewerRef, handle);
      if (handle && prev?.handle !== handle) {
        const saved = positions.current.get(fileId);
        if (saved) handle.restorePosition(saved);
        else if (!shown.current.has(fileId)) {
          // The top of that page in any layout (a 'spread' position lands at its page's top).
          const { env: e, pageCount: n } = live.current;
          const page = e.initialPage?.(fileId);
          if (typeof page === 'number' && Number.isFinite(page) && page > 0 && n > 1) {
            handle.restorePosition({ page: Math.min(n - 1, Math.floor(page)), top: 0, left: 0, zoom: 1, layout: 'spread' });
          }
        }
        shown.current.add(fileId);
      }
    },
    [fileId, viewerRef],
  );

  return (
    <SheetViewer
      key={`${file.id}:${file.rev}`}
      ref={setHandle}
      file={file}
      mode={mode}
      active={active}
      connectors={allConnectors}
      renderPageOverlay={renderPageOverlay}
      onStaleRevision={onStaleRevision}
      pinsInteractive={pinsInteractive}
      onPinTap={onPinTap}
      editTouchAction={editTouchAction}
      onPageChange={onPageChange}
    />
  );
}
